import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "../lib/api";

interface TranscodingItem {
  id: string;
  status: string;
  storagePath: string;
  transcodedPath: string | null;
  viewUrl: string | null;
  uploadedBy: { id: string; displayName: string | null };
  createdAt: string;
}

interface TranscodingResponse {
  media: TranscodingItem[];
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  pending: { label: "Pending", color: "var(--c-text-subtle)" },
  transcoding: { label: "Converting…", color: "var(--c-accent)" },
  failed: { label: "Failed", color: "var(--c-danger)" },
};

export function TranscodingAdmin({ familySlug }: { familySlug: string }) {
  const queryClient = useQueryClient();

  const query = useQuery<TranscodingResponse>({
    queryKey: ["admin-transcoding", familySlug],
    queryFn: () =>
      apiFetch<TranscodingResponse>(
        `/families/${familySlug}/admin/transcoding`,
      ),
    refetchInterval: 10_000,
  });

  const cancel = useMutation({
    mutationFn: (mediaId: string) =>
      apiFetch(`/families/${familySlug}/admin/transcoding/${mediaId}/cancel`, {
        method: "POST",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin-transcoding", familySlug],
      });
    },
  });

  const remove = useMutation({
    mutationFn: (mediaId: string) =>
      apiFetch(`/families/${familySlug}/admin/transcoding/${mediaId}`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["admin-transcoding", familySlug],
      });
    },
  });

  const items = query.data?.media ?? [];

  if (query.isLoading) {
    return <p className="text-muted">Loading…</p>;
  }

  if (items.length === 0) {
    return (
      <p className="text-muted">
        No pending or failed video conversions.
      </p>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--s-2)" }}>
      {items.map((item) => {
        const st = STATUS_LABELS[item.status] ?? STATUS_LABELS.pending!;
        const filename = item.storagePath.split("/").pop() ?? item.id.slice(0, 8);
        return (
          <div
            key={item.id}
            className="card"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "var(--s-3)",
              flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span
                  style={{
                    display: "inline-block",
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: st.color,
                    flexShrink: 0,
                  }}
                />
                <strong style={{ fontSize: "0.9rem" }}>{filename}</strong>
                <span
                  style={{
                    fontSize: "0.8rem",
                    color: st.color,
                    fontWeight: 600,
                  }}
                >
                  {st.label}
                </span>
              </div>
              <div
                className="text-subtle"
                style={{ fontSize: "0.8rem", marginTop: 2 }}
              >
                Uploaded by {item.uploadedBy.displayName ?? "Unknown"} · {timeAgo(item.createdAt)}
              </div>
              <div
                className="text-subtle"
                style={{
                  fontSize: "0.75rem",
                  marginTop: 2,
                  fontFamily: "monospace",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  maxWidth: 400,
                }}
              >
                {item.id}
              </div>
            </div>

            <div style={{ display: "flex", gap: "var(--s-2)" }}>
              {item.status === "transcoding" && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => cancel.mutate(item.id)}
                  disabled={cancel.isPending}
                  style={{ fontSize: "0.85rem", padding: "6px 14px", minHeight: 34 }}
                >
                  {cancel.isPending ? "…" : "Cancel"}
                </button>
              )}
              <button
                type="button"
                className="btn-tertiary"
                onClick={() => {
                  if (window.confirm(`Delete this media? This removes the file permanently.`)) {
                    remove.mutate(item.id);
                  }
                }}
                disabled={remove.isPending}
                style={{
                  fontSize: "0.85rem",
                  padding: "6px 14px",
                  minHeight: 34,
                  color: "var(--c-danger)",
                }}
              >
                {remove.isPending ? "…" : "Delete"}
              </button>
            </div>
          </div>
        );
      })}
      {(cancel.error || remove.error) && (
        <p className="text-error">
          {(cancel.error ?? remove.error)?.message}
        </p>
      )}
    </div>
  );
}
